import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Calendar, 
  MapPin, 
  Mountain, 
  ChevronDown, 
  ChevronUp,
  Footprints,
  Flag,
  Compass,
  Tent,
  Sunrise,
  TreePine
} from "lucide-react";
import DateFormatterComponent from "../../utils/DateFormatterComponent";

const TimelineRedesign = ({ adventures }) => {
  const [advList, setAdvList] = useState([]);

  useEffect(() => {
    if (adventures && adventures.length > 0) {
      
      const sortedAdventures = [...adventures].sort((a, b) => {
        // Support both nested (adventure_data) and flat structure
        const dateA = new Date(a?.adventure_data?.start_date || a?.start_date);
        const dateB = new Date(b?.adventure_data?.start_date || b?.start_date);
        return dateA - dateB;
      });

      const newAdvList = sortedAdventures.map((adventure, index) => {
        // Support both nested (adventure_data) and flat structure
        const advData = adventure?.adventure_data || adventure;
        return {
          id: advData?.id,
          title: advData?.destination_data?.name,
          description: advData?.destination_data?.description,
          startDate: advData?.start_date,
          endDate: advData?.end_date,
          position: index % 2 === 0 ? "left" : "right",
          stepNumber: index + 1,
        };
      });

      setAdvList(newAdvList);
    }
  }, [adventures]);
  return (
    <div className="relative w-full mx-auto py-8">
      {/* SVG Definitions for gradients */}
      <svg className="hidden lg:block absolute inset-0 w-0 h-0">
        <defs>
          <linearGradient id="trailGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#2D4671" />
            <stop offset="50%" stopColor="#5BC0EB" />
            <stop offset="100%" stopColor="#FDE68A" />
          </linearGradient>
        </defs>
      </svg>

      {/* Floating Mountain Icons for atmosphere */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-5">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${Math.random() * 80 + 10}%`,
              top: `${Math.random() * 80 + 10}%`,
            }}
            animate={{
              y: [0, -20, 0],
              rotate: [0, 10, 0],
            }}
            transition={{
              duration: 3 + i,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <TreePine className="w-12 h-12 text-mamba-navy" />
          </motion.div>
        ))}
      </div>

      {/* Timeline Items */}
      <div className="relative">
        {/* Vertical Connecting Line */}
        <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-mamba-navy via-mamba-sky-blue to-mamba-golden transform -translate-x-1/2" />
        {advList.map((item, index) => (
          <TimelineItem key={item.id} item={item} index={index} totalItems={advList.length} />
        ))}
        
        {/* Final Summit Flag */}
        {advList.length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex justify-center"
          >
            <div className="relative">
              <motion.div
                animate={{
                  y: [-5, 5, -5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="w-20 h-20 rounded-full bg-gradient-to-br from-mamba-navy to-mamba-sky-blue flex items-center justify-center shadow-2xl"
              >
                <Flag className="w-10 h-10 text-white" />
              </motion.div>
              <div className="text-center mt-4">
                <p className="text-lg font-black font-montserrat text-mamba-navy">Journey Complete!</p>
                <p className="text-sm font-inter text-mamba-navy/70">Summit Reached</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

const TimelineItem = ({ item, index, totalItems }) => {
  const [showFull, setShowFull] = useState(false);
  const isLeft = item.position === "left";
  // Different icons for different stages of the hike
  const getStageIcon = (stepNumber, total) => {
    if (stepNumber === 1) return Sunrise; // Start
    if (stepNumber === total) return Flag; // End
    if (stepNumber % 3 === 0) return Tent; // Camp
    if (stepNumber % 2 === 0) return Compass; // Waypoint
    return Mountain; // Regular peak
  };
  
  const StageIcon = getStageIcon(item.stepNumber, totalItems);

  const getText = (desc) => {
    if (typeof desc === "string") return desc;
    return "";
  };

  const text = getText(item.description);
  const words = text.split(/\s+/);
  const shouldTruncate = words.length > 25;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="relative"
      style={{ marginBottom: '80px' }}
    >
      {/* Desktop: Full width alternating layout */}
      <div className="hidden lg:block">
        <div className="grid grid-cols-2 gap-0 items-center relative">
          {/* Left Side */}
          <div className={`${isLeft ? 'pr-12' : ''}`}>
            {isLeft && (
              <div className="relative">
                <ContentCard item={item} showFull={showFull} setShowFull={setShowFull} shouldTruncate={shouldTruncate} words={words} />
                
                {/* Horizontal connecting line to center */}
                <div className="absolute top-1/2 right-0 transform translate-x-full -translate-y-1/2" style={{ width: '48px' }}>
                  <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.15 + 0.3 }}
                    className="h-0.5 bg-gradient-to-r from-mamba-navy to-mamba-sky-blue origin-left"
                  />
                  {/* Animated footprint traveling on line */}
                  <motion.div
                    initial={{ x: 0, opacity: 0 }}
                    animate={{ 
                      x: [0, 48, 48],
                      opacity: [0, 1, 0]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear",
                      delay: index * 0.4
                    }}
                    className="absolute top-1/2 left-0 transform -translate-y-1/2"
                  >
                    <Footprints className="w-3 h-3 text-mamba-sky-blue" />
                  </motion.div>
                </div>
              </div>
            )}
          </div>

          {/* Center - Checkpoint */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
            {/* Footprints above checkpoint */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.3 }}
              transition={{ delay: index * 0.2 }}
              className="absolute -top-12 left-1/2 transform -translate-x-1/2 z-20"
            >
              <Footprints className="w-5 h-5 text-mamba-navy" />
            </motion.div>
            
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              whileInView={{ scale: 1, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.15,
                type: "spring",
                stiffness: 200 
              }}
              whileHover={{ 
                scale: 1.15,
                rotate: [0, -10, 10, -10, 0],
                transition: { duration: 0.5 }
              }}
              className="relative cursor-pointer z-20"
            >
              {/* Outer Glow Ring */}
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0.2, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute inset-0 rounded-full bg-mamba-sky-blue blur-xl"
              />
              
              {/* Main Checkpoint Node */}
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-mamba-navy via-mamba-sky-blue to-mamba-golden flex items-center justify-center shadow-2xl border-4 border-white">
                {/* Icon Badge */}
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center border-2 border-mamba-golden">
                  <span className="text-mamba-navy font-black text-xs font-montserrat">
                    {String(item.stepNumber).padStart(2, '0')}
                  </span>
                </div>
                
                {/* Dynamic Stage Icon */}
                <StageIcon className="w-8 h-8 text-white" />
              </div>
            </motion.div>

          </div>

          {/* Right Side */}
          <div className={`${!isLeft ? 'pl-12' : ''}`}>
            {!isLeft && (
              <div className="relative">
                <ContentCard item={item} showFull={showFull} setShowFull={setShowFull} shouldTruncate={shouldTruncate} words={words} />
                
                {/* Horizontal connecting line to center */}
                <div className="absolute top-1/2 left-0 transform -translate-x-full -translate-y-1/2" style={{ width: '48px' }}>
                  <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.15 + 0.3 }}
                    className="h-0.5 bg-gradient-to-l from-mamba-navy to-mamba-sky-blue origin-right"
                  />
                  {/* Animated footprint traveling on line */}
                  <motion.div
                    initial={{ x: 0, opacity: 0 }}
                    animate={{ 
                      x: [0, -48, -48],
                      opacity: [0, 1, 0]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear",
                      delay: index * 0.4
                    }}
                    className="absolute top-1/2 right-0 transform -translate-y-1/2"
                  >
                    <Footprints className="w-3 h-3 text-mamba-sky-blue" />
                  </motion.div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

        {/* Mobile: Always show on left with step indicator */}
        <div className="lg:hidden">
          <div className="flex items-start gap-4">
            {/* Mobile Step Indicator with Icon */}
            <motion.div 
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 200, delay: index * 0.1 }}
              className="flex-shrink-0 relative"
            >
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-mamba-navy to-mamba-sky-blue flex items-center justify-center shadow-lg">
                <StageIcon className="w-7 h-7 text-white" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center border-2 border-mamba-golden">
                <span className="text-mamba-navy font-black text-xs font-montserrat">
                  {item.stepNumber}
                </span>
              </div>
            </motion.div>
            
            {/* Mobile Card */}
            <div className="flex-1">
              <ContentCard item={item} showFull={showFull} setShowFull={setShowFull} shouldTruncate={shouldTruncate} words={words} />
            </div>
          </div>
        </div>
    </motion.div>
  );
};

const isValidDate = (date) => {
  if (!date) return false;
  const d = new Date(date);
  return d instanceof Date && !isNaN(d);
};

const ContentCard = ({ item, showFull, setShowFull, shouldTruncate, words }) => {
  const hasStartDate = isValidDate(item.startDate);
  const hasEndDate = isValidDate(item.endDate);
  const hasAnyDate = hasStartDate || hasEndDate;

  return (
    <motion.div
      whileHover={{ y: -8 }}
      className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border-2 border-mamba-sky-blue/20 hover:border-mamba-golden transition-all duration-300 group"
    >
      {/* Icon Badge */}
      <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 bg-mamba-golden/20 rounded-full border border-mamba-golden/30">
        <Mountain className="w-4 h-4 text-mamba-navy" />
        <span className="text-xs font-bold font-montserrat uppercase tracking-wider text-mamba-navy">
          Adventure
        </span>
      </div>

      {/* Title */}
      <h3 className="text-2xl md:text-3xl font-black font-montserrat text-mamba-navy mb-4 tracking-tight group-hover:text-mamba-sky-blue transition-colors duration-300">
        {item.title}
      </h3>

      {/* Description */}
      <div className="mb-6">
        <p className="text-slate-600 font-inter leading-relaxed text-sm md:text-base">
          {showFull || !shouldTruncate ? (
            item.description
          ) : (
            <>
              {words.slice(0, 25).join(" ")}...
              <button
                className="ml-2 text-mamba-sky-blue hover:text-mamba-navy font-semibold underline text-sm inline-flex items-center gap-1"
                onClick={() => setShowFull(true)}
                type="button"
              >
                Read more
                <ChevronDown className="w-4 h-4" />
              </button>
            </>
          )}
          {showFull && shouldTruncate && (
            <button
              className="ml-2 text-mamba-sky-blue hover:text-mamba-navy font-semibold underline text-sm inline-flex items-center gap-1"
              onClick={() => setShowFull(false)}
              type="button"
            >
              Show less
              <ChevronUp className="w-4 h-4" />
            </button>
          )}
        </p>
      </div>

      {/* Date Range - Only show if at least one date is valid */}
      {hasAnyDate && (
        <div className="flex flex-wrap items-center gap-4 p-4 bg-gradient-to-r from-mamba-bg-light to-white rounded-xl border border-mamba-navy/20">
          {hasStartDate && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-mamba-sky-blue" />
              <div>
                <div className="text-xs text-mamba-navy/70 font-semibold font-montserrat uppercase">Start</div>
                <div className="text-sm font-bold font-inter text-mamba-navy">
                  <DateFormatterComponent date={item.startDate} />
                </div>
              </div>
            </div>
          )}
          
          {hasStartDate && hasEndDate && (
            <div className="text-slate-400">→</div>
          )}
          
          {hasEndDate && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-mamba-golden" />
              <div>
                <div className="text-xs text-mamba-navy/70 font-semibold font-montserrat uppercase">End</div>
                <div className="text-sm font-bold font-inter text-mamba-navy">
                  <DateFormatterComponent date={item.endDate} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default TimelineRedesign;
